"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import {
  BookOpenIcon,
  HomeIcon,
  RssIcon,
  UserPenIcon,
  LogIn,
  Search,
  LibraryBig,
  PanelLeftClose,
  PanelLeft,
  GraduationCap,
  SwatchBook,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import { NavGroup } from "@/type/navigation";
import { useUser } from "@/lib/context/user-context";
import { UserDropdown } from "./user-dropdown";
import { LanguageToggle } from "@/components/language-toggle";
import { useI18n } from "@/lib/i18n";

type BaseNavItemKey = "home" | "search" | "episodes" | "course" | "library" | "review" | "history" | "settings";

type BaseNavItem = {
  key: BaseNavItemKey;
  url: string;
  icon?: NavGroup["items"][number]["icon"];
  target?: NavGroup["items"][number]["target"];
};

type BaseNavGroup = {
  title: string;
  url: string;
  items: BaseNavItem[];
};

const baseNavLinks: BaseNavGroup[] = [
  {
    title: "",
    url: "#",
    items: [
      {
        key: "home",
        url: "/",
        icon: HomeIcon,
      },
      {
        key: "search",
        url: "/search",
        icon: Search,
      },
      {
        key: "episodes",
        url: "/episodes",
        icon: RssIcon,
      },
      {
        key: "course",
        url: "/course",
        icon: GraduationCap,
      },
      // {
      //   title: "Store",
      //   url: "/store",
      //   icon: ShoppingCartIcon,
      // },
      // {
      //   title: "About",
      //   url: "/about",
      //   icon: SquareUserRoundIcon,
      // },
      {
        key: "library",
        url: "/library",
        icon: BookOpenIcon,
      },
      {
        key: "review",
        url: "/review",
        icon: SwatchBook,
      },
      {
        key: "history",
        url: "/history",
        icon: LibraryBig,
      },
      // {
      //   title: "About",
      //   url: "/landing",
      //   icon: InfoIcon,
      // },
      {
        key: "settings",
        url: "/settings",
        icon: UserPenIcon,
      },
    ],
  },
  // {
  //   title: "Social",
  //   url: "#",
  //   items: [
  //     // {
  //     //   title: "Twitter",
  //     //   url: "https://x.com/TobyBelhome",
  //     //   icon: TwitterIcon,
  //     //   target: "_blank",
  //     // },
  //     // {
  //     //   title: "Facebook",
  //     //   url: "https://www.facebook.com/",
  //     //   icon: FacebookIcon,
  //     //   target: "_blank",
  //     // },
  //     // {
  //     //   title: "Dribbble",
  //     //   url: "https://dribbble.com/",
  //     //   icon: DribbbleIcon,
  //     //   target: "_blank",
  //     // },
  //   ],
  // },
];

export default function Navigation() {
  const pathname = usePathname();
  const replacedPathname = pathname.split("/").filter(Boolean)[0];
  const { user } = useUser();
  const { toggleSidebar, state } = useSidebar();
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useI18n();

  // Check if user is Basic Plan Lite
  const isBasicPlanLite = user?.user_metadata?.isBasicPlanLite === true;
  const navLinks = useMemo<NavGroup[]>(() => {
    return baseNavLinks.map((group) => ({
      title: group.title,
      url: group.url,
      items: group.items
        .filter((navItem) => !(isBasicPlanLite && navItem.key === "search"))
        .map((navItem) => ({
          title: t(`navigation.links.${navItem.key}`),
          url: navItem.url,
          icon: navItem.icon,
          target: navItem.target,
        })),
    }));
  }, [t, isBasicPlanLite]);

  return (
    <Sidebar className="!border-e-0 p-3" collapsible="icon">
      <SidebarHeader className="mb-10">
        <SidebarMenu>
          <SidebarMenuItem 
            className="flex items-center space-x-4 group-data-[collapsible=icon]:justify-center relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Logo Container with Hover Effect */}
            <div className="sm:size-16 size-12 flex items-center justify-center pt-1 sm:pt-0 relative">
              {/* Logo - hidden when hovering */}
              <img 
                src="/logo1.png" 
                alt="Logo" 
                className={`w-full h-full object-contain transition-opacity duration-200 ${
                  isHovered ? 'opacity-0' : 'opacity-100'
                }`}
              />
              
              {/* Toggle Button - shown when hovering */}
              <button
                onClick={toggleSidebar}
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 hover:scale-105 ${
                  isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                title={state === "expanded" ? t("navigation.actions.collapse") : t("navigation.actions.expand")}
              >
                {state === "expanded" ? (
                  <PanelLeftClose className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
                ) : (
                  <PanelLeft className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
                )}
              </button>
            </div>
            
            <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
              <span className="font-semibold text-2xl">Lingoflows</span>
              <span className="text-[12px]">{t("navigation.tagline")}</span>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="space-y-6">
          {navLinks.map((item, index) => (
            <div key={`nav-group-${index}`}>
              {item.title ? (
                <SidebarGroupLabel>{item.title}</SidebarGroupLabel>
              ) : null}
              <SidebarGroupContent>
                <SidebarMenu className="group-data-[collapsible=icon]:items-center">
                  {item?.items &&
                    item.items.map((item) => (
                      <SidebarMenuItem key={item.url} className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center w-full">
                        <SidebarMenuButton
                          asChild
                          className="text-base rounded-lg px-3 h-auto data-[active=true]:bg-background data-[active=true]:!font-semibold group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center"
                          isActive={
                            (item.url === "/" &&
                              replacedPathname === undefined) ||
                            item.url.replace("/", "") === replacedPathname
                          }
                          tooltip={item.title}
                        >
                          <Link href={item.url} target={item.target} className="group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                            {item.icon && <item.icon className="!size-5" />}
                            <span className="group-data-[collapsible=icon]:sr-only">{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </div>
          ))}
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex flex-col gap-2">
          {/* Language Toggle - above theme toggle */}
          <div className="flex justify-center mb-2">
            <LanguageToggle collapsed={state === "collapsed"} />
          </div>
          {/* Theme Toggle */}
          <div className="flex justify-center mb-2">
            <ThemeToggle />
          </div>
          
          {/* User Dropdown - show if user is logged in */}
          {user && (
            <div className="flex justify-center">
              <UserDropdown />
            </div>
          )}
          
          {/* Login Button - only show if user is not logged in */}
          {!user && (
            <div className="flex justify-center">
              <SidebarMenuButton
                asChild
                className="text-base rounded-lg h-auto px-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center"
                tooltip={t("common.signIn")}
              >
                <Link href="/login" className="group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                  <LogIn className="w-6 h-6" />
                  <span className="group-data-[collapsible=icon]:sr-only">{t("common.signIn")}</span>
                </Link>
              </SidebarMenuButton>
            </div>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
